import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Buildings, Users, FolderOpen, MapPin, Clock, ArrowUpRight,
  Ruler, ShareNetwork, Bell, CreditCard, ChartBar
} from '@phosphor-icons/react';

export default function DashboardPage() {
  const { user, apiCall } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [recentProjects, setRecentProjects] = useState([]);
  const [planInfo, setPlanInfo] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const promises = [apiCall('get', '/dashboard/stats')];
      if (user?.role !== 'super_admin') promises.push(apiCall('get', '/projects'));
      if (user?.tenant_id) promises.push(apiCall('get', `/billing/check-limits/${user.tenant_id}`));
      if (user?.role !== 'regular_user') promises.push(apiCall('get', '/audit-logs?limit=5'));

      const results = await Promise.all(promises);
      setStats(results[0].data);
      if (user?.role !== 'super_admin' && results[1]) setRecentProjects((results[1].data || []).slice(0, 6));
      if (user?.tenant_id && results.length > 2) setPlanInfo(results[2]?.data);
      const actIdx = user?.role !== 'super_admin' ? 3 : 2;
      if (results[actIdx]) setRecentActivity(results[actIdx]?.data?.logs || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  const role = user?.role || 'regular_user';

  if (loading) return <div className="flex items-center justify-center h-full"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  // Build stat cards based on role
  const statCards = [];
  if (role === 'super_admin') {
    statCards.push(
      { label: 'Total Tenants', value: stats.tenants || 0, icon: Buildings, color: 'bg-primary/10 text-primary' },
      { label: 'Total Users', value: stats.users || 0, icon: Users, color: 'bg-blue-500/10 text-blue-500' },
      { label: 'Total Projects', value: stats.projects || 0, icon: FolderOpen, color: 'bg-emerald-500/10 text-emerald-500' },
    );
  } else if (role === 'tenant_admin') {
    statCards.push(
      { label: 'Team Members', value: stats.users || 0, icon: Users, color: 'bg-primary/10 text-primary', limit: planInfo?.limits?.max_users },
      { label: 'Projects', value: stats.projects || 0, icon: FolderOpen, color: 'bg-emerald-500/10 text-emerald-500', limit: planInfo?.limits?.max_projects },
      { label: 'Measurements', value: recentProjects.reduce((s, p) => s + (p.measurements?.length || 0), 0), icon: Ruler, color: 'bg-amber-500/10 text-amber-500' },
    );
  } else {
    statCards.push(
      { label: 'My Projects', value: stats.projects || 0, icon: FolderOpen, color: 'bg-primary/10 text-primary' },
      { label: 'Measurements', value: recentProjects.reduce((s, p) => s + (p.measurements?.length || 0), 0), icon: Ruler, color: 'bg-emerald-500/10 text-emerald-500' },
    );
  }

  return (
    <div className="p-4 lg:p-6 space-y-5 animate-fadeIn" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h4 className="text-lg lg:text-xl font-semibold" data-testid="dashboard-heading">
          Welcome back, {user?.name?.split(' ')[0] || 'User'}
        </h4>
        <p className="text-muted-foreground text-sm mt-0.5">Here's your overview</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4" data-testid="stats-grid">
        {statCards.map((sc, i) => {
          const Icon = sc.icon;
          const isUnlimited = sc.limit >= 999999;
          return (
            <Card key={i} className="shadow-card rounded-lg border-0" data-testid={`stat-card-${i}`}>
              <CardContent className="p-4 lg:p-5">
                <div className="flex items-center justify-between mb-2 lg:mb-3">
                  <div className={`w-9 h-9 lg:w-[42px] lg:h-[42px] rounded-md flex items-center justify-center ${sc.color}`}>
                    <Icon size={20} weight="duotone" />
                  </div>
                  {sc.limit && !isUnlimited && (
                    <span className="text-[10px] font-medium text-muted-foreground">{sc.value}/{sc.limit}</span>
                  )}
                </div>
                <h3 className="text-2xl lg:text-[28px] font-bold leading-none mb-0.5">{sc.value}</h3>
                <p className="text-xs lg:text-[13px] text-muted-foreground">{sc.label}</p>
                {sc.limit && !isUnlimited && (
                  <div className="mt-2 h-1.5 bg-secondary rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${sc.value / sc.limit >= 0.8 ? 'bg-amber-500' : 'bg-primary'}`} style={{ width: `${Math.min(100, (sc.value / sc.limit) * 100)}%` }} />
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Plan Info Card - for tenant users */}
      {planInfo && role !== 'super_admin' && (
        <Card className="shadow-card rounded-lg border-0">
          <CardContent className="p-4 lg:p-5 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center">
                <CreditCard size={20} className="text-primary" weight="duotone" />
              </div>
              <div>
                <span className="text-sm font-semibold">Current Plan: <Badge className="capitalize ml-1 text-xs" variant="outline">{planInfo.plan}</Badge></span>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {planInfo.usage.projects}/{planInfo.limits.max_projects >= 999999 ? '∞' : planInfo.limits.max_projects} projects,{' '}
                  {planInfo.usage.users}/{planInfo.limits.max_users >= 999999 ? '∞' : planInfo.limits.max_users} users
                </p>
              </div>
            </div>
            {role === 'tenant_admin' && (
              <button onClick={() => navigate('/my-billing')} className="text-xs text-primary font-medium hover:underline flex items-center gap-1">
                Manage Plan <ArrowUpRight size={12} />
              </button>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Recent Projects */}
        {role !== 'super_admin' && (
          <div className="lg:col-span-2">
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-sm font-semibold">Recent Projects</h5>
              <button onClick={() => navigate('/projects')} className="text-xs text-primary font-medium hover:underline">View All</button>
            </div>
            {recentProjects.length === 0 ? (
              <Card className="shadow-card rounded-lg border-0">
                <CardContent className="p-8 text-center text-muted-foreground">
                  <MapPin size={36} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm font-medium">No projects yet</p>
                  <p className="text-xs mt-1">Create your first project to start measuring.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {recentProjects.map((p, i) => (
                  <Card key={p.id || i} className="shadow-card rounded-lg border-0 hover:shadow-card-hover transition-shadow cursor-pointer" onClick={() => navigate(`/project/${p.id}`)} data-testid={`project-card-${i}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="w-8 h-8 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                          <FolderOpen size={16} className="text-primary" weight="duotone" />
                        </div>
                        <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock size={10} />{p.created_at ? new Date(p.created_at).toLocaleDateString('en-GB') : ''}</span>
                      </div>
                      <h6 className="font-semibold text-sm mb-0.5 truncate">{p.name}</h6>
                      <p className="text-xs text-muted-foreground line-clamp-1">{p.description || 'No description'}</p>
                      <div className="mt-2 flex items-center gap-3 text-[10px] text-muted-foreground">
                        <span className="flex items-center gap-1"><Ruler size={10} />{(p.measurements || []).length}</span>
                        {p.shared_with?.length > 0 && <span className="flex items-center gap-1"><ShareNetwork size={10} />{p.shared_with.length}</span>}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Activity Feed */}
        {role !== 'regular_user' && (
          <div className={role === 'super_admin' ? 'lg:col-span-3' : ''}>
            <div className="flex items-center justify-between mb-3">
              <h5 className="text-sm font-semibold">Recent Activity</h5>
              <button onClick={() => navigate('/audit-logs')} className="text-xs text-primary font-medium hover:underline">View All</button>
            </div>
            <Card className="shadow-card rounded-lg border-0">
              <CardContent className="p-0">
                {recentActivity.length === 0 ? (
                  <div className="p-6 text-center text-muted-foreground text-xs">No recent activity</div>
                ) : (
                  <div className="divide-y divide-border">
                    {recentActivity.map((log, i) => (
                      <div key={log.id || `${log.action}-${log.created_at}-${i}`} className="flex items-center gap-3 px-4 py-3">
                        <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                          <ChartBar size={13} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate"><span className="font-semibold">{log.user_name}</span> {log.action?.replace(/_/g, ' ')}</p>
                          {log.details && <p className="text-[10px] text-muted-foreground truncate">{log.details}</p>}
                        </div>
                        <span className="text-[10px] text-muted-foreground shrink-0 whitespace-nowrap">
                          {log.created_at ? new Date(log.created_at).toLocaleDateString('en-GB') : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
